import { produce } from 'immer';

import { Bonus, CachedBonus, CachedBonuses } from '../../../../../types/bonus';

export interface ProfileBonusPage {
	bonuses: Bonus[];
	metadata: { page_number: number; page_size: number } | undefined;
	hasMore: boolean;
	pageNumber: number;
}

export interface CachedProfileBonuses {
	pages: ProfileBonusPage[];
	pageParams: unknown[];
}

export const addBonusToFeed = (bonus: Bonus, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}
	return produce(cached, (draft) => {
		draft.pages[0].data.result.unshift(bonus);
	});
};

export const updateCachedBonus = (bonus: Bonus, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	const updatedBonus = {
		...bonus,
		family_amount: bonus.amount + (bonus.child_bonuses?.reduce((acc, child) => acc + child.amount, 0) ?? 0),
	};

	return produce(cached, (draft) => {
		draft.pages = draft.pages.map((page) => {
			const index = page.data.result.findIndex((record) => [bonus.id, bonus._id].includes(record.id));
			if (index !== -1) {
				page.data.result[index] = {
					...page.data.result[index],
					...updatedBonus,
				};
			}
			return page;
		});
	});
};

export const updateCachedComment = (bonus: Bonus, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.pages = draft.pages.map((page) => {
			const index = page.data.result.findIndex((record) => bonus.parent_bonus_id === record.id);

			if (index !== -1) {
				page.data.result[index] = {
					...page.data.result[index],
					child_bonuses: page.data.result[index].child_bonuses.map((child) => {
						if (child.id === bonus.id) {
							return {
								...child,
								...bonus,
							};
						}

						return child;
					}),
				};
			}
			return page;
		});
	});
};

export const removeBonusFromFeed = (id: string, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.pages = draft.pages.map((page) => {
			return produce(page, (draft) => {
				draft.data.result = draft.data.result.filter((bonus) => bonus.id !== id);
			});
		});
	});
};

export const removeCommentFromFeed = (id: string, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		for (const page of draft.pages) {
			for (const bonus of page.data.result) {
				if (!bonus.child_bonuses?.length) continue;

				const prevLength = bonus.child_bonuses.length;
				bonus.child_bonuses = bonus.child_bonuses.filter((child) => child.id !== id);

				if (bonus.child_bonuses.length !== prevLength) {
					const family_amount = bonus.amount + bonus.child_bonuses.reduce((acc, child) => acc + child.amount, 0);
					bonus.family_amount = family_amount;
					bonus.child_bonuses = bonus.child_bonuses.map((child) => ({ ...child, family_amount }));
				}
			}
		}
	});
};

export const addBonusToParent = (bonus: Bonus, cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.pages = draft.pages.map((page) => {
			const index = page.data.result.findIndex((record) => record.id === bonus.parent_bonus_id);
			if (index !== -1) {
				page.data.result[index].child_bonuses.push(bonus);
				const family_amount = page.data.result[index].amount + page.data.result[index].child_bonuses.reduce((acc, child) => acc + child.amount, 0);
				page.data.result[index].family_amount = family_amount;
				page.data.result[index].child_bonuses = page.data.result[index].child_bonuses.map((child) => ({
					...child,
					family_amount,
				}));
			}

			return page;
		});
	});
};

export const addBonusToParentInProfile = (bonus: Bonus, cached?: CachedProfileBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		for (const page of draft.pages) {
			const index = page.bonuses.findIndex((record) => record.id === bonus.parent_bonus_id);
			if (index !== -1) {
				const parent = page.bonuses[index];
				if (!parent.child_bonuses) parent.child_bonuses = [];
				parent.child_bonuses.push(bonus);
				const family_amount = parent.amount + parent.child_bonuses.reduce((acc, child) => acc + child.amount, 0);
				parent.family_amount = family_amount;
				parent.child_bonuses = parent.child_bonuses.map((child) => ({
					...child,
					family_amount,
				}));
			}
		}
	});
};

export const updateCommentInProfile = (bonus: Bonus, cached?: CachedProfileBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		for (const page of draft.pages) {
			const index = page.bonuses.findIndex((record) => bonus.parent_bonus_id === record.id);
			if (index !== -1) {
				const parent = page.bonuses[index];
				if (!parent.child_bonuses) continue;
				parent.child_bonuses = parent.child_bonuses.map((child) => {
					if (child.id === bonus.id) {
						return { ...child, ...bonus };
					}
					return child;
				});
				const family_amount = parent.amount + parent.child_bonuses.reduce((acc, child) => acc + child.amount, 0);
				parent.family_amount = family_amount;
				parent.child_bonuses = parent.child_bonuses.map((child) => ({ ...child, family_amount }));
			}
		}
	});
};

export const removeCommentFromProfile = (id: string, cached?: CachedProfileBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		for (const page of draft.pages) {
			for (const bonus of page.bonuses) {
				if (!bonus.child_bonuses?.length) continue;

				const prevLength = bonus.child_bonuses.length;
				bonus.child_bonuses = bonus.child_bonuses.filter((child) => child.id !== id);

				if (bonus.child_bonuses.length !== prevLength) {
					const family_amount = bonus.amount + bonus.child_bonuses.reduce((acc, child) => acc + child.amount, 0);
					bonus.family_amount = family_amount;
					bonus.child_bonuses = bonus.child_bonuses.map((child) => ({ ...child, family_amount }));
				}
			}
		}
	});
};

export const updateBonusReactions = (newBonus: Bonus, cached?: CachedBonus) => {
	if (!cached) {
		return cached;
	}
	return produce(cached, (draft) => {
		draft.data.result = draft.data.result.map((result) => {
			if (result.id === newBonus.id) {
				result.reactions = [...(newBonus.reactions ?? [])];
			}
			return result;
		});
	});
};

export const addCommentToSingleBonus = (comment: Bonus, cached?: CachedBonus) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.data.result = draft.data.result.map((result) => {
			if (result.id === comment.parent_bonus_id) {
				result.child_bonuses = [...result.child_bonuses, comment];
			}

			return result;
		});
	});
};

export const updateCachedReactions = (bonusId: string, reactions: Bonus['reactions'], cached?: CachedBonuses) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.pages = draft.pages.map((page) => {
			return produce(page, (draft) => {
				draft.data.result = draft.data.result.map((result) => {
					// Check if this is the bonus we're looking for
					if (result.id === bonusId) {
						return {
							...result,
							reactions: [...(reactions ?? [])],
						};
					}
					// Check if the bonus is a child bonus (comment/add-on)
					if (result.child_bonuses?.some((c) => c.id === bonusId)) {
						return {
							...result,
							child_bonuses: result.child_bonuses.map((child) => {
								if (child.id === bonusId) {
									return {
										...child,
										reactions: [...(reactions ?? [])],
									};
								}
								return child;
							}),
						};
					}
					return result;
				});
			});
		});
	});
};

export const updateSingleBonusReactions = (bonusId: string, reactions: Bonus['reactions'], cached?: CachedBonus) => {
	if (!cached) {
		return cached;
	}

	return produce(cached, (draft) => {
		draft.data.result = draft.data.result.map((result) => {
			// Check if this is the bonus we're looking for
			if (result.id === bonusId) {
				return {
					...result,
					reactions: [...(reactions ?? [])],
				};
			}
			// Check if the bonus is a child bonus (comment/add-on)
			if (result.child_bonuses?.some((c) => c.id === bonusId)) {
				return {
					...result,
					child_bonuses: result.child_bonuses.map((child) => {
						if (child.id === bonusId) {
							return {
								...child,
								reactions: [...(reactions ?? [])],
							};
						}
						return child;
					}),
				};
			}
			return result;
		});
	});
};
